import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import i18n from '@/js/i18n.js'
import { f7 } from 'framework7-vue'
import { openAdaptiveModal } from './useAdaptiveModal.js'
import { useAuthStore } from '../pinia-stores/auth.js'
import { useAccountStore } from '../pinia-stores/account.js'
import { useInvitationStore } from '../pinia-stores/invitation.js'
import { useLocationStore } from '../pinia-stores/location.js'
import { useDesktopScreen, useTabletScreen } from './useMediaQuery.js'

export function useInvitation(options = {}) {
  const {
    f7route,
    f7router,
    entityId,
    entityType,
    entity,
    isAdmin,
    memberList,
    invitationToUpdate: invitationToUpdateOption,
    description: descriptionOption,
    endDate: endDateOption,
    invited: invitedOption,
    invitationType: invitationTypeOption,
    recipients: recipientsOption,
    visibleItem: visibleItemOption,
  } = options

  const t = i18n.global.t

  const authStore = useAuthStore()
  const accountStore = useAccountStore()
  const invitationStore = useInvitationStore()
  const locationStore = useLocationStore()

  const profile = computed(() => authStore.profile)
  const isDeskTop = useDesktopScreen()
  const isTablet = useTabletScreen().matches
  const locale = computed(() => authStore.locale)
  const documentsUrl = computed(() => authStore.documentsUrl)
  const document = computed(() => authStore.document)
  const isNativePlatform = computed(() => authStore.isNativePlatform)

  const userDetail = computed(() => accountStore.userDetail)
  const userAccount = computed(() => accountStore.userAccount)
  const users = computed(() => accountStore.users)

  const invitationList = computed(() => invitationStore.invitationList)
  const invitation = computed(() => invitationStore.invitation)

  const destinationCity = computed(() => locationStore.destinationCity)
  const country = computed(() => locationStore.country)

  const typeText = computed(() => {
    const _invitationType = invitationTypeOption?.value ?? getCurrentInstance()?.proxy?.oneTime
    return _invitationType
      ? t('common.oneTime')
      : t('common.multiple')
  })

  function getVal(key, option, self) {
    if (option?.value !== undefined) return option.value
    return self?.[key]
  }

  function setVal(key, option, self, value) {
    if (option?.value !== undefined) {
      option.value = value
    } else if (self) {
      self[key] = value
    }
  }

  function getGroupInvitationList() {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _entityId = getVal('entityId', entityId, self?.entityId)
    const _entityType = getVal('entityType', entityType, self?.entityType)

    invitationStore.getGroupInvitationList({
      entityId: _entityId,
      domainType: _entityType,
    })
  }

  function loadMore(done) {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _entityId = getVal('entityId', entityId, self?.entityId)
    const _entityType = getVal('entityType', entityType, self?.entityType)

    setTimeout(() => {
      const _visibleItem = getVal('visibleItem', visibleItemOption, self?.visibleItem)
      setVal('visibleItem', visibleItemOption, self, (_visibleItem || 0) + 30)
      invitationStore.getGroupInvitationList({
        entityId: _entityId,
        domainType: _entityType,
      })
      done()
    }, 1000)
  }

  function openUpdateInvite(item) {
    const instance = getCurrentInstance()
    const self = instance?.proxy

    setVal('invitationToUpdate', invitationToUpdateOption, self, item)
    setVal('description', descriptionOption, self, item.description)
    setVal('endDate', endDateOption, self, item.validUntil)
    setVal('invited', invitedOption, self, item.invited)
    setVal('invitationType', invitationTypeOption, self, item.type)
    setVal('recipients', recipientsOption, self, '')
    openAdaptiveModal('.updateInvitationsPopup')
  }

  function shareInvitation(item) {
    const sharedContent = {
      message: item.description,
      subject: item.groupName,
      files: [item.qr, 'https://avocash.com/34x34.png'],
      short: false,
      linkBase: '',
      url: item.url,
    }
    const instance = getCurrentInstance()
    const self = instance?.proxy
    if (self?.socialShare) {
      self.socialShare(sharedContent)
    }
  }

  function handleRecipientsInput(value) {
    const instance = getCurrentInstance()
    const self = instance?.proxy

    setVal('recipients', recipientsOption, self, value)
    const _recipients = getVal('recipients', recipientsOption, self?.recipients)
    if (_recipients && _recipients.endsWith(';')) {
      const newRecipient = _recipients.slice(0, -1).trim()
      const _invited = getVal('invited', invitedOption, self?.invited) || []
      if (newRecipient && isValidRecipient(newRecipient)) {
        setVal('invited', invitedOption, self, [..._invited, newRecipient])
        setVal('recipients', recipientsOption, self, '')
      } else if (newRecipient) {
        setVal('recipients', recipientsOption, self, newRecipient)
      } else {
        setVal('recipients', recipientsOption, self, '')
      }
    }
  }

  function textChange() {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _recipients = getVal('recipients', recipientsOption, self?.recipients)
    if (!_recipients) return

    const users_list = _recipients
    const invitedUsers = users_list
      .split(/[,;]/)
      .map((item) => item.trim())
      .filter((item) => item && isValidRecipient(item))

    const invalidUsers = users_list
      .split(/[,;]/)
      .map((item) => item.trim())
      .filter((item) => item && !isValidRecipient(item))

    const _invited = getVal('invited', invitedOption, self?.invited) || []
    setVal('invited', invitedOption, self, [..._invited, ...invitedUsers])
    setVal('recipients', recipientsOption, self, invalidUsers.join(', '))
  }

  function isValidRecipient(value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const phoneRegex = /^\+?[0-9\s\-()]{7,20}$/
    return emailRegex.test(value) || phoneRegex.test(value)
  }

  function deleteInvitedChip(item) {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _invited = getVal('invited', invitedOption, self?.invited) || []
    const index = _invited.findIndex((user) => user === item)
    if (index === -1) return
    setVal('invited', invitedOption, self, _invited.filter((user) => user !== item))
  }

  function updateInvitation() {
    const instance = getCurrentInstance()
    const self = instance?.proxy

    const data = {
      type: getVal('invitationType', invitationTypeOption, self?.invitationType) === 'oneTime' ? 'oneTime' : 'multiple',
      description: getVal('description', descriptionOption, self?.description),
      validUntil: getVal('endDate', endDateOption, self?.endDate),
      invited: getVal('invited', invitedOption, self?.invited),
    }
    invitationStore.updateInvitation(data)
  }

  function acceptInvitation(invitationId) {
    invitationStore.acceptInvitation({ invitationId })
  }

  function changeInvitationStatus(invitationId, status) {
    invitationStore.changeInvitationStatus({ invitationId, status })
  }

  function deleteInvitation(invitationId) {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _isAdmin = isAdmin?.value ?? self?.isAdmin

    invitationStore.deleteInvitation({
      invitationId,
      isAdmin: _isAdmin,
    })
  }

  async function createNewInvitation() {
    try {
      const instance = getCurrentInstance()
      const self = instance?.proxy
      const _entityId = getVal('entityId', entityId, self?.entityId)
      const _entity = getVal('entity', entity, self?.entity)
      const _entityType = getVal('entityType', entityType, self?.entityType)

      const data = {
        groupId: _entityId,
        groupName: _entity?.name,
        groupType: _entityType,
        userName: userAccount.value?.name || profile.value?.name,
        userId: userAccount.value?.userId || profile.value?.sub,
        type: getVal('invitationType', invitationTypeOption, self?.invitationType) === 'oneTime' ? 'oneTime' : 'multiple',
        description: getVal('description', descriptionOption, self?.description),
        validUntil: getVal('endDate', endDateOption, self?.endDate)?.[0] ?? getVal('endDate', endDateOption, self?.endDate),
        invited: getVal('invited', invitedOption, self?.invited),
      }

      await invitationStore.createNewInvitation(data)
      const _invitation = invitation.value
      if (_invitation) {
        shareInvitation(_invitation)
      }
    } catch (error) {
      f7.dialog.alert(t('common.messageNotSent'))
    }
  }

  function goToinvitation(item) {
    const instance = getCurrentInstance()
    const self = instance?.proxy
    const _f7router = f7router ?? self?.$f7router
    const _entityId = getVal('entityId', entityId, self?.entityId)
    const _entity = getVal('entity', entity, self?.entity)
    const _isAdmin = isAdmin?.value ?? self?.isAdmin
    const _memberList = memberList?.value ?? self?.memberList

    _f7router?.navigate(`/invite/${item.invitationId}`, {
      props: {
        entityId: _entityId,
        entity: _entity,
        isAdmin: _isAdmin,
        memberList: _memberList,
        invite: item,
      },
    })
  }

  return {
    profile,
    isDeskTop,
    isTablet,
    locale,
    documentsUrl,
    document,
    isNativePlatform,
    userDetail,
    userAccount,
    users,
    invitationList,
    invitation,
    destinationCity,
    country,
    typeText,
    getGroupInvitationList,
    loadMore,
    openUpdateInvite,
    shareInvitation,
    handleRecipientsInput,
    textChange,
    isValidRecipient,
    deleteInvitedChip,
    updateInvitation,
    acceptInvitation,
    changeInvitationStatus,
    deleteInvitation,
    createNewInvitation,
    goToinvitation,
  }
}
